import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import HeroTemplate from "components/sectionTemplates/Hero"
import Callout from "components/TextElements/Callout"
import AnimatedPath from "components/UI-Related/AnimatedPaths"
import { ReactComponent as HeroPathsSVG } from "images/b2b/heroPaths.svg"
import colors from "styles/colors"
import media from "styles/media"
import shadows from "styles/shadows"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import { returnContactUsLink } from "utils/routes"
import useMedia from "utils/useMedia"

export default function Hero() {
  const titleWidth = useMedia("516px", "31.084vw", "58.69vw", "90vw")
  const textWidth = useMedia("516px", "100%", "58.496vw", "90vw")
  const { t, language } = useI18next()

  const images: Queries.B2BHeroQuery = useStaticQuery(graphql`
    query B2BHero {
      image1: file(relativePath: { eq: "b2b/hero1.webp" }) {
        ...LocalImageQuery
      }
      image2: file(relativePath: { eq: "b2b/Check.webp" }) {
        ...LocalImageQuery
      }
      image1M: file(relativePath: { eq: "b2b/hero1M.webp" }) {
        ...LocalImageQuery
      }
      image2M: file(relativePath: { eq: "b2b/heroCheckM.webp" }) {
        ...LocalImageQuery
      }
    }
  `)

  const [HeroImage1, HeroImage2, HeroImage1M, HeroImage2M] = [
    images.image1?.childImageSharp?.gatsbyImageData,
    images.image2?.childImageSharp?.gatsbyImageData,
    images.image1M?.childImageSharp?.gatsbyImageData,
    images.image2M?.childImageSharp?.gatsbyImageData,
  ]

  const image1 = useMedia(HeroImage1, HeroImage1, HeroImage1, HeroImage1M)
  const image2 = useMedia(HeroImage2, HeroImage2, HeroImage2, HeroImage2M)

  const buttonText = t("Let's Talk Business")
  const contactLink = returnContactUsLink( language );
  const title = t("It’s time B2B payments caught up with B2C.")

  return (
    <HeroTemplate
      bgColor={colors.seaFoam10}
      lightMode
      leftChildren={
        <Callout
          variant="lightmode"
          kicker={{
            text: t("B2B Payments & Banking") ?? "",
            alternateStyle: true,
            gradient: colors.seaFoam500,
          }}
          title={{
            content: title,
            size: useMedia(text.h3, text.h3, text.h3, text.h1Mobile),
            width: titleWidth,
            color: colors.blue300,
          }}
          text={{
            color: colors.blue300,
            width: textWidth,
            content: t(
              "Digital B2B payments aren’t just nice to have. They’ve become an expectation of doing business. Galileo can help you build a B2B payments platform that offers both buyers and suppliers flexible, secure options for creating and processing commercial payments—while driving value for both you and your customers."
            ),
          }}
          buttons={[
            {
              text: buttonText,
              arrow: true,
              route: contactLink,
            },
          ]}
        />
      }
      rightChildren={
        <ImagePathWrapper>
          <HeroPathsWrapper>
            <AnimatedPath>
              <HeroPaths />
            </AnimatedPath>
          </HeroPathsWrapper>

          {image2 && (
            <Image2
              loading="eager"
              image={image2}
              alt={t("check illustration")}
            />
          )}
          {image1 && (
            <Image1
              loading="eager"
              image={image1}
              alt={t("overview ui illustration")}
            />
          )}
        </ImagePathWrapper>
      }
    />
  )
}

const ImagePathWrapper = styled.div`
  position: relative;

  ${fresponsive(css`
    width: 736px;
    height: 920px;
  `)}

  ${ftablet(css`
    width: 649px;
    height: 623px;
    margin: -102px 0 0 198px;
  `)}

  ${fmobile(css`
    width: 497px;
    height: 404px;
    margin: 40px 0 188px;
  `)}
`

const HeroPathsWrapper = styled.div`
  position: absolute;

  ${fresponsive(css`
    width: 440px;
    height: 281px;
    left: 141px;
    top: 241px;
  `)}

  ${ftablet(css`
    width: 499px;
    height: 324px;
    left: 28px;
    top: 70px;
  `)}

  ${fmobile(css`
    width: 399px;
    height: 259px;
    left: -76px;
  `)}
`

const HeroPaths = styled(HeroPathsSVG)`
  position: absolute;

  ${fresponsive(css`
    width: 440px;
    height: 281px;
  `)}

  ${fmobile(css`
    width: 399px;
    height: 259px;
  `)}
`

const Image2 = styled(GatsbyImage)`
  object-fit: cover;
  position: absolute;
  right: 0;
  top: 12.048vw;

  z-index: 2;
  ${shadows.cardMockupShadow};

  ${fresponsive(css`
    width: 450px;
    height: 210px;
    right: 0;
    top: 200px;
    border-radius: 10px;
  `)}

  ${ftablet(css`
    width: 450px;
    height: 210px;
    top: 0;
    right: 0;
    left: unset;
  `)}

  ${fmobile(css`
    object-fit: unset;
    width: 360px;
    height: 168px;
    left: 106px;
    top: 0vw;
    z-index: 0;
  `)}
`

const Image1 = styled(GatsbyImage)`
  object-fit: cover;
  position: absolute;
  z-index: 3;

  ${fresponsive(css`
    width: 424px;
    height: 441px;
    left: 20px;
    top: 382px;
  `)}

  ${ftablet(css`
    width: 450px;
    height: 210px;
    top: 0;
    right: 0;
    left: unset;
  `)}

  ${fmobile(css`
    object-fit: unset;
    width: 360px;
    height: 168px;
    left: 106px;
    top: 0vw;
    z-index: 0;
  `)}
  ${media.fullWidth} {
    width: 424px;
    height: 441px;
    left: 20px;
    top: 382px;
  }

  ${media.tablet} {
    width: 27.051vw;
    height: 35.449vw;
    left: 5.957vw;
    top: 17.773vw;
  }

  ${media.mobile} {
    object-fit: unset;
    width: 59.093vw;
    height: 77.44vw;
    left: 0;
    top: 39.36vw;
    z-index: 1;
  }
`
